(function () {
  'use strict';

  /** Discount codes per bundle tier (Shopify Admin). */
  var TIER_DISCOUNT_CODES = {
    tier1: 'BUNDLEB2G1',
    tier2: 'BUNDLEB3G2',
  };

  function getShopifyCartRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function cartHasDiscount(cart, code) {
    if (!cart) return false;
    if (cart.total_discount > 0) return true;

    var normalizedCode = String(code || '').toUpperCase();

    if (cart.cart_level_discount_applications) {
      for (var i = 0; i < cart.cart_level_discount_applications.length; i++) {
        var app = cart.cart_level_discount_applications[i];
        var title = String(app.title || '').toUpperCase();
        if (title === normalizedCode || title.indexOf(normalizedCode) !== -1) return true;
      }
    }

    if (cart.items) {
      for (var j = 0; j < cart.items.length; j++) {
        var item = cart.items[j];
        if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
          return true;
        }
        if (item.discounts) {
          for (var k = 0; k < item.discounts.length; k++) {
            var discount = item.discounts[k];
            var discountTitle = String(discount.title || '').toUpperCase();
            if (discountTitle === normalizedCode || discountTitle.indexOf(normalizedCode) !== -1) {
              return true;
            }
          }
        }
      }
    }

    return false;
  }

  class BundleSection {
    constructor(root) {
      this.root = root;
      this.sectionId = root.getAttribute('data-section-id');

      this.galleryEl = root.querySelector('[data-gallery]');
      this.formEl = root.querySelector('.sbh-bundle__form');
      this.slides = Array.from(root.querySelectorAll('[data-slide]'));
      this.thumbs = Array.from(root.querySelectorAll('[data-thumb]'));
      this.slideObserver = null;
      this.formResizeObserver = null;
      this.handleWindowResize = null;
      this.desktopMQ = window.matchMedia('(min-width: 992px)');

      this.tiers = Array.from(root.querySelectorAll('[data-tier]'));
      this.counterEl = root.querySelector('[data-counter]');

      this.productRows = Array.from(root.querySelectorAll('[data-product-row]')).map((row) => ({
        el: row,
        variantId: row.getAttribute('data-variant-id') || '',
        valueEl: row.querySelector('[data-qty-value]'),
        minusEl: row.querySelector('[data-qty-minus]'),
        plusEl: row.querySelector('[data-qty-plus]'),
        quantity: parseInt(row.getAttribute('data-default-qty'), 10) || 0,
      }));

      this.submitBtn = root.querySelector('[data-submit]');
      this.statusEl = root.querySelector('[data-status]');
      this.defaultSubmitText = this.submitBtn ? this.submitBtn.textContent.trim() : 'Add to cart';

      this.selectedTier = null;

      this.init();
    }

    init() {
      this.bindGallery();
      this.bindTiers();
      this.bindQuantities();
      this.bindSubmit();
      this.bindGalleryHeightSync();

      const defaultTier = this.tiers.find((t) => t.hasAttribute('data-tier-default')) || this.tiers[0];
      if (defaultTier) this.setTier(defaultTier.getAttribute('data-tier'));

      this.renderQuantities();
      this.renderCounter();
      this.renderSubmitButton();
    }

    bindGalleryHeightSync() {
      if (!this.galleryEl || !this.formEl) return;

      this.syncGalleryHeight();

      this.handleWindowResize = () => this.syncGalleryHeight();
      window.addEventListener('resize', this.handleWindowResize);

      if ('ResizeObserver' in window) {
        this.formResizeObserver = new ResizeObserver(() => this.syncGalleryHeight());
        this.formResizeObserver.observe(this.formEl);
      }
    }

    syncGalleryHeight() {
      if (!this.galleryEl || !this.formEl) return;
      if (this.desktopMQ.matches) {
        this.galleryEl.style.height = this.formEl.offsetHeight + 'px';
      } else {
        this.galleryEl.style.height = '';
      }
    }

    /* ---------- Gallery (thumbnails sync with native scroll) ---------- */

    bindGallery() {
      if (!this.galleryEl || this.thumbs.length === 0) return;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const index = parseInt(thumb.getAttribute('data-thumb'), 10);
          this.scrollToSlide(index);
        });
      });

      if ('IntersectionObserver' in window && this.slides.length > 0) {
        this.slideObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const index = parseInt(entry.target.getAttribute('data-slide'), 10);
                this.setActiveThumb(index);
              }
            });
          },
          { root: this.galleryEl, threshold: 0.6 }
        );
        this.slides.forEach((slide) => this.slideObserver.observe(slide));
      }
    }

    scrollToSlide(index) {
      const target = this.slides[index];
      if (!target || !this.galleryEl) return;
      target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }

    setActiveThumb(index) {
      this.thumbs.forEach((thumb) => {
        const i = parseInt(thumb.getAttribute('data-thumb'), 10);
        const isActive = i === index;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    /* ---------- Tiers ---------- */

    bindTiers() {
      this.tiers.forEach((tier) => {
        tier.addEventListener('click', () => {
          this.setTier(tier.getAttribute('data-tier'));
        });
      });
    }

    setTier(tierKey) {
      this.selectedTier = tierKey;
      this.tiers.forEach((tier) => {
        const isActive = tier.getAttribute('data-tier') === tierKey;
        tier.classList.toggle('is-active', isActive);
        tier.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      this.renderQuantities();
      this.renderCounter();
      this.renderSubmitButton();
    }

    getActiveTierTarget() {
      const activeTier = this.tiers.find((t) => t.getAttribute('data-tier') === this.selectedTier);
      if (!activeTier) return 0;
      const total = parseInt(activeTier.getAttribute('data-tier-total'), 10);
      return isNaN(total) ? 0 : total;
    }

    /* ---------- Quantities ---------- */

    bindQuantities() {
      this.productRows.forEach((row) => {
        if (row.minusEl) {
          row.minusEl.addEventListener('click', () => {
            row.quantity = Math.max(0, row.quantity - 1);
            this.renderQuantities();
            this.renderCounter();
            this.renderSubmitButton();
          });
        }
        if (row.plusEl) {
          row.plusEl.addEventListener('click', () => {
            if (this.isAtCap()) return;
            row.quantity = row.quantity + 1;
            this.renderQuantities();
            this.renderCounter();
            this.renderSubmitButton();
          });
        }
      });
    }

    getTotalQuantity() {
      return this.productRows.reduce((sum, r) => sum + r.quantity, 0);
    }

    isAtCap() {
      const target = this.getActiveTierTarget();
      if (target <= 0) return false;
      return this.getTotalQuantity() >= target;
    }

    renderQuantities() {
      const atCap = this.isAtCap();
      this.productRows.forEach((row) => {
        if (row.valueEl) row.valueEl.textContent = String(row.quantity);
        if (row.minusEl) row.minusEl.disabled = row.quantity <= 0;
        if (row.plusEl) row.plusEl.disabled = atCap;
      });
    }

    renderCounter() {
      if (!this.counterEl) return;
      const total = this.getTotalQuantity();
      const target = this.getActiveTierTarget();
      this.counterEl.textContent = target > 0 ? total + '/' + target + ' added' : total + ' added';
    }

    /* ---------- Submit ---------- */

    isBundleComplete() {
      const target = this.getActiveTierTarget();
      if (target <= 0) return false;
      return this.getTotalQuantity() === target;
    }

    renderSubmitButton() {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = !this.isBundleComplete();
    }

    getDiscountCodeForTier() {
      if (!this.selectedTier) return null;
      var activeTier = this.tiers.find((t) => t.getAttribute('data-tier') === this.selectedTier);
      if (activeTier) {
        var fromDom = activeTier.getAttribute('data-tier-discount');
        if (fromDom) return fromDom.trim();
      }
      return TIER_DISCOUNT_CODES[this.selectedTier] || null;
    }

    /**
     * Applies a discount code via Shopify Cart AJAX API (cart/update.js).
     * The legacy /discount/{code} fetch path does not reliably set the cart session for AJAX drawers.
     */
    applyBundleDiscount() {
      var code = this.getDiscountCodeForTier();
      if (!code) return Promise.resolve(false);

      var cartRoot = getShopifyCartRoot();

      return fetch(cartRoot + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ discount: code }),
      })
        .then(function (response) {
          return response.json().then(function (cart) {
            if (!response.ok) {
              var message =
                (cart && (cart.description || cart.message)) ||
                'Discount could not be applied.';
              throw new Error(message);
            }
            return cart;
          });
        })
        .then(function (cart) {
          return cartHasDiscount(cart, code);
        })
        .catch(function (err) {
          console.warn('[bundle-section] discount apply failed:', err);
          return false;
        });
    }

    bindSubmit() {
      if (!this.submitBtn) return;
      this.submitBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }

    setStatus(text, state) {
      if (!this.statusEl) return;
      this.statusEl.textContent = text || '';
      this.statusEl.classList.remove('is-error', 'is-success');
      if (state === 'error') this.statusEl.classList.add('is-error');
      if (state === 'success') this.statusEl.classList.add('is-success');
    }

    buildItems() {
      const items = [];
      this.productRows.forEach((row) => {
        if (row.quantity <= 0) return;
        if (!row.variantId) return;
        items.push({ id: String(row.variantId), quantity: row.quantity });
      });
      return items;
    }

    addToCart() {
      if (!this.isBundleComplete()) {
        const target = this.getActiveTierTarget();
        this.setStatus('Please add all ' + target + ' products to complete your bundle.', 'error');
        return;
      }

      const items = this.buildItems();

      this.submitBtn.disabled = true;
      this.submitBtn.textContent = 'Adding...';
      this.setStatus('', null);

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: items }),
      })
        .then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            const message = data && data.description ? data.description : 'Could not add to cart.';
            throw new Error(message);
          }
          return data;
        })
        .then(() => this.applyBundleDiscount())
        .then((discountApplied) => {
          if (discountApplied) {
            this.setStatus('Added to cart with bundle discount.', 'success');
          } else {
            this.setStatus('Added to cart. Bundle discount could not be applied.', 'error');
          }
          return this.notifyCartUpdated();
        })
        .catch((err) => {
          this.setStatus(err.message || 'Could not add to cart.', 'error');
        })
        .finally(() => {
          this.submitBtn.textContent = this.defaultSubmitText;
          this.renderSubmitButton();
        });
    }

    notifyCartUpdated() {
      var updatePromise = Promise.resolve();

      if (window.liquidAjaxCart && typeof window.liquidAjaxCart.update === 'function') {
        var updateResult = window.liquidAjaxCart.update();
        if (updateResult && typeof updateResult.then === 'function') {
          updatePromise = updateResult;
        }
      }

      return updatePromise.then(() => {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:updated'));

        if (window.skCartScrollLock && typeof window.skCartScrollLock.open === 'function') {
          window.skCartScrollLock.open();
        } else {
          document.documentElement.classList.add('js-show-ajax-cart');
          document.body.classList.add('js-show-ajax-cart');
          document.documentElement.style.overflow = 'hidden';
          document.body.style.overflow = 'hidden';
        }
      });
    }

    destroy() {
      // Listeners attached to elements inside the section root are
      // garbage-collected when Shopify replaces the section HTML.
      // Observers and window listeners must be cleaned up explicitly.
      if (this.slideObserver) {
        this.slideObserver.disconnect();
        this.slideObserver = null;
      }
      if (this.formResizeObserver) {
        this.formResizeObserver.disconnect();
        this.formResizeObserver = null;
      }
      if (this.handleWindowResize) {
        window.removeEventListener('resize', this.handleWindowResize);
        this.handleWindowResize = null;
      }
    }
  }

  const instances = new WeakMap();

  function initAll(scope) {
    const root = scope || document;
    root.querySelectorAll('[data-section-bundle]').forEach((el) => {
      if (instances.has(el)) return;
      instances.set(el, new BundleSection(el));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Theme editor support.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target) initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!event.target) return;
    event.target.querySelectorAll('[data-section-bundle]').forEach((el) => {
      const instance = instances.get(el);
      if (instance) {
        instance.destroy();
        instances.delete(el);
      }
    });
  });
})();
